// Static US geography for the sentiment map: TopoJSON states, the national
// outline, FIPS lookups, and the join from a sentiment-map `path` to a state.
// Projection + centroids are computed in the renderer (they depend on canvas
// size). Phase 1 is state-level; county/city joins come in Phase 2/3.
import { type GeoPermissibleObjects, geoContains } from 'd3-geo';
import type { Feature, FeatureCollection, MultiLineString } from 'geojson';
import { feature, mesh } from 'topojson-client';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import statesTopo from 'us-atlas/states-10m.json';

// us-atlas Topology — typed loosely to avoid pulling topojson-specification.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const topo = statesTopo as any;

export const states = feature(
  topo,
  topo.objects.states,
) as unknown as FeatureCollection;
export const outerMesh = mesh(
  topo,
  topo.objects.states,
  (a: unknown, b: unknown) => a === b,
) as MultiLineString;

export const byFips: Record<string, Feature> = {};
for (const f of states.features) byFips[String(f.id)] = f;

// 2-letter state abbreviation → 2-digit FIPS (the sentiment-map `path` carries
// the abbreviation, e.g. "US/TX/Montgomery/The Woodlands").
export const STATE_ABBR_TO_FIPS: Record<string, string> = {
  AL: '01',
  AK: '02',
  AZ: '04',
  AR: '05',
  CA: '06',
  CO: '08',
  CT: '09',
  DE: '10',
  DC: '11',
  FL: '12',
  GA: '13',
  HI: '15',
  ID: '16',
  IL: '17',
  IN: '18',
  IA: '19',
  KS: '20',
  KY: '21',
  LA: '22',
  ME: '23',
  MD: '24',
  MA: '25',
  MI: '26',
  MN: '27',
  MS: '28',
  MO: '29',
  MT: '30',
  NE: '31',
  NV: '32',
  NH: '33',
  NJ: '34',
  NM: '35',
  NY: '36',
  NC: '37',
  ND: '38',
  OH: '39',
  OK: '40',
  OR: '41',
  PA: '42',
  RI: '44',
  SC: '45',
  SD: '46',
  TN: '47',
  TX: '48',
  UT: '49',
  VT: '50',
  VA: '51',
  WA: '53',
  WV: '54',
  WI: '55',
  WY: '56',
};

/** State FIPS for a sentiment-map region path ("US/TX/...") → "48", or null. */
export function stateFipsFromPath(path: string): string | null {
  const abbr = path.split('/')[1];
  return abbr ? (STATE_ABBR_TO_FIPS[abbr] ?? null) : null;
}

// 2-digit FIPS → full state name, for the zoom breadcrumb ("United States ▸ Texas").
export const STATE_NAME_BY_FIPS: Record<string, string> = {
  '01': 'Alabama',
  '02': 'Alaska',
  '04': 'Arizona',
  '05': 'Arkansas',
  '06': 'California',
  '08': 'Colorado',
  '09': 'Connecticut',
  '10': 'Delaware',
  '11': 'District of Columbia',
  '12': 'Florida',
  '13': 'Georgia',
  '15': 'Hawaii',
  '16': 'Idaho',
  '17': 'Illinois',
  '18': 'Indiana',
  '19': 'Iowa',
  '20': 'Kansas',
  '21': 'Kentucky',
  '22': 'Louisiana',
  '23': 'Maine',
  '24': 'Maryland',
  '25': 'Massachusetts',
  '26': 'Michigan',
  '27': 'Minnesota',
  '28': 'Mississippi',
  '29': 'Missouri',
  '30': 'Montana',
  '31': 'Nebraska',
  '32': 'Nevada',
  '33': 'New Hampshire',
  '34': 'New Jersey',
  '35': 'New Mexico',
  '36': 'New York',
  '37': 'North Carolina',
  '38': 'North Dakota',
  '39': 'Ohio',
  '40': 'Oklahoma',
  '41': 'Oregon',
  '42': 'Pennsylvania',
  '44': 'Rhode Island',
  '45': 'South Carolina',
  '46': 'South Dakota',
  '47': 'Tennessee',
  '48': 'Texas',
  '49': 'Utah',
  '50': 'Vermont',
  '51': 'Virginia',
  '53': 'Washington',
  '54': 'West Virginia',
  '55': 'Wisconsin',
  '56': 'Wyoming',
};

// US Census regions — the v1 "filter / finesse" grain (above states, below
// country). Foreign-vs-domestic filtering is the same recombination once the
// world hierarchy adds non-US regions; until then this is the available axis.
export type CensusRegion = 'Northeast' | 'Midwest' | 'South' | 'West';
export const CENSUS_REGIONS: CensusRegion[] = [
  'Northeast',
  'Midwest',
  'South',
  'West',
];

export const CENSUS_REGION_BY_FIPS: Record<string, CensusRegion> = {
  // Northeast
  '09': 'Northeast',
  '23': 'Northeast',
  '25': 'Northeast',
  '33': 'Northeast',
  '44': 'Northeast',
  '50': 'Northeast',
  '34': 'Northeast',
  '36': 'Northeast',
  '42': 'Northeast',
  // Midwest
  '17': 'Midwest',
  '18': 'Midwest',
  '26': 'Midwest',
  '39': 'Midwest',
  '55': 'Midwest',
  '19': 'Midwest',
  '20': 'Midwest',
  '27': 'Midwest',
  '29': 'Midwest',
  '31': 'Midwest',
  '38': 'Midwest',
  '46': 'Midwest',
  // South
  '10': 'South',
  '12': 'South',
  '13': 'South',
  '24': 'South',
  '37': 'South',
  '45': 'South',
  '51': 'South',
  '11': 'South',
  '54': 'South',
  '01': 'South',
  '21': 'South',
  '28': 'South',
  '47': 'South',
  '05': 'South',
  '22': 'South',
  '40': 'South',
  '48': 'South',
  // West
  '04': 'West',
  '08': 'West',
  '16': 'West',
  '30': 'West',
  '32': 'West',
  '35': 'West',
  '49': 'West',
  '56': 'West',
  '02': 'West',
  '06': 'West',
  '15': 'West',
  '41': 'West',
  '53': 'West',
};

/** FIPS of the state containing [lon, lat], or null (point off the US). */
export function stateFipsAtLonLat(
  lonlat: [number, number] | null,
): string | null {
  if (!lonlat) return null;
  for (const f of states.features) {
    if (geoContains(f as GeoPermissibleObjects, lonlat)) return String(f.id);
  }
  return null;
}
